/**
 * Utility functions for image handling and FormData creation
 */

/**
 * Get file extension from file type
 * @param {string} fileType - The MIME type of the file
 * @returns {string} - File extension
 */
export const getFileExtension = (fileType) => {
  const typeMap = {
    'image/jpeg': 'jpeg',
    'image/jpg': 'jpeg',
    'image/png': 'png',
    'image/gif': 'gif',
    'image/webp': 'webp'
  };
  
  return typeMap[fileType] || 'jpeg';
};

/**
 * Validate image file
 * @param {File} file - The file to validate
 * @param {Object} options - Validation options
 * @returns {Object} - Validation result
 */
export const validateImageFile = (file, options = {}) => {
  const {
    maxSize = 5 * 1024 * 1024, // 5MB default
    acceptedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
  } = options;

  if (!file || !(file instanceof File)) {
    return { isValid: false, error: 'Invalid file provided' };
  }

  if (!acceptedTypes.includes(file.type)) {
    return { 
      isValid: false, 
      error: `File type ${file.type} is not supported. Accepted types: ${acceptedTypes.join(', ')}` 
    };
  }

  if (file.size > maxSize) {
    return { 
      isValid: false, 
      error: `File size ${(file.size / (1024 * 1024)).toFixed(2)}MB exceeds maximum size ${(maxSize / (1024 * 1024)).toFixed(2)}MB` 
    };
  }

  if (file.size === 0) {
    return { isValid: false, error: 'File is empty or corrupted' };
  }

  return { isValid: true };
};

/**
 * Create FormData payload for image upload API
 * @param {File} file - The image file
 * @returns {FormData} - FormData payload ready for upload
 */
export const createImageUploadFormData = (file) => {
  const validation = validateImageFile(file);

  console.log("Preparing to upload:", file.name);
  if (!validation.isValid) {
    throw new Error(validation.error);
  }

  const formData = new FormData();
  const fileExtension = getFileExtension(file.type);
  
  // Add file and upload type as expected by API
  formData.append('file', file);
  formData.append('uploadType', fileExtension);
  
  return formData;
};
